import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { AttachmentModel, AttachmentAssignmentModel, AttachmentOwnerType, StorageType } from '../../../core/models/app.models';
import { AttachmentService } from '../../../core/services/attachment.service';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-attachment-picker',
  templateUrl: './attachment-picker.component.html',
  styleUrls: ['./attachment-picker.component.scss']
})
export class AttachmentPickerComponent implements OnInit, OnChanges {
  @Input() ownerType!: AttachmentOwnerType;
  @Input() ownerId!: number;
  @Input() assignments: AttachmentAssignmentModel[] = [];
  @Input() readonly = false;
  @Output() assignmentsChange = new EventEmitter<AttachmentAssignmentModel[]>();

  // Modal state
  showPicker = false;

  // Available files
  availableFiles: AttachmentModel[] = [];
  loadingFiles = false;
  searchTerm = '';
  searchSubject = new Subject<string>();

  // Upload
  uploading = false;
  dragOver = false;

  constructor(
    public attachmentService: AttachmentService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchTerm = term;
      this.loadAvailableFiles();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['assignments']) {
      // Sort by assignedAt descending
      this.assignments = [...(changes['assignments'].currentValue || [])].sort((a, b) =>
        new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime()
      );
    }
  }

  get attachments(): AttachmentModel[] {
    return this.assignments
      .filter(a => a.attachment)
      .map(a => a.attachment!);
  }

  // Modal controls
  openPicker() {
    if (this.readonly) return;
    this.showPicker = true;
    this.loadAvailableFiles();
  }

  closePicker() {
    this.showPicker = false;
    this.searchTerm = '';
  }

  // Search
  onSearchChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.searchSubject.next(value);
  }

  // Load files
  loadAvailableFiles() {
    this.loadingFiles = true;
    this.attachmentService.getAll(this.searchTerm).subscribe({
      next: (files) => {
        // Filter out already assigned files
        const assignedIds = this.assignments.map(a => a.attachmentId);
        this.availableFiles = files.filter(f => !assignedIds.includes(f.id!));
        this.loadingFiles = false;
      },
      error: () => {
        this.loadingFiles = false;
        this.messageService.add({ severity: 'error', summary: 'Chyba', detail: 'Nepodařilo se načíst soubory' });
      }
    });
  }

  // Assign existing file
  assignFile(file: AttachmentModel) {
    this.attachmentService.assign(file.id!, this.ownerType, this.ownerId).subscribe({
      next: (assignment) => {
        assignment.attachment = file;
        this.assignments = [assignment, ...this.assignments];
        this.assignmentsChange.emit(this.assignments);
        this.availableFiles = this.availableFiles.filter(f => f.id !== file.id);
        this.messageService.add({ severity: 'success', summary: 'Hotovo', detail: 'Soubor byl přiřazen' });
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Chyba', detail: 'Nepodařilo se přiřadit soubor' });
      }
    });
  }

  // Unassign file
  unassignFile(assignment: AttachmentAssignmentModel) {
    this.attachmentService.unassign(assignment.id!).subscribe({
      next: () => {
        this.assignments = this.assignments.filter(a => a.id !== assignment.id);
        this.assignmentsChange.emit(this.assignments);
        if (assignment.attachment) {
          this.availableFiles = [assignment.attachment, ...this.availableFiles];
        }
        this.messageService.add({ severity: 'success', summary: 'Hotovo', detail: 'Přiřazení bylo odstraněno' });
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Chyba', detail: 'Nepodařilo se odstranit přiřazení' });
      }
    });
  }

  // Upload new file
  onFileDrop(event: DragEvent) {
    event.preventDefault();
    this.dragOver = false;

    if (event.dataTransfer?.files?.length) {
      this.uploadFile(event.dataTransfer.files[0]);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave() {
    this.dragOver = false;
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.uploadFile(input.files[0]);
      input.value = '';
    }
  }

  uploadFile(file: File) {
    this.uploading = true;
    this.attachmentService.upload(this.ownerType, this.ownerId, StorageType.Local, file).subscribe({
      next: (assignment) => {
        this.assignments = [assignment, ...this.assignments];
        this.assignmentsChange.emit(this.assignments);
        this.uploading = false;
        this.messageService.add({ severity: 'success', summary: 'Hotovo', detail: 'Soubor byl nahrán' });
      },
      error: () => {
        this.uploading = false;
        this.messageService.add({ severity: 'error', summary: 'Chyba', detail: 'Nepodařilo se nahrát soubor' });
      }
    });
  }

  // Check if file is an image
  isImage(file: AttachmentModel): boolean {
    return file.mimeType?.startsWith('image/') || false;
  }

  // Get file icon
  getFileIcon(file: AttachmentModel): string {
    return this.attachmentService.getFileIcon(file.mimeType);
  }

  // Format file size
  formatSize(bytes: number): string {
    return this.attachmentService.formatFileSize(bytes);
  }

  // Track by function
  trackByAssignment(index: number, item: AttachmentAssignmentModel): number {
    return item.id || index;
  }

  trackByFile(index: number, item: AttachmentModel): number {
    return item.id || index;
  }
}
